import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Building2, DollarSign, FileText, Save, AlertCircle, CheckCircle, MapPin, Hash, Receipt, Eye, Calendar, User } from 'lucide-react';
import axios from 'axios';

const PaystubSettings = () => {
  const { user, token, canAccessManager, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('settings'); // 'settings' or 'paystubs'
  const [paystubs, setPaystubs] = useState([]);
  const [allPaystubs, setAllPaystubs] = useState([]); // Store all paystubs for filtering
  const [paystubFilter, setPaystubFilter] = useState('latest_10'); // Filter state

  // Handle URL parameters for tab navigation
  useEffect(() => {
    const activeTabParam = searchParams.get('activeTab');
    if (activeTabParam === 'paystubs' || activeTabParam === 'settings') {
      setActiveTab(activeTabParam);
    }
  }, [searchParams]);
  const [config, setConfig] = useState({
    // Tax Rates
    social_security_rate: 6.2,
    medicare_rate: 1.45,
    federal_income_tax_rate: 12.0,
    state: '',
    state_income_tax_rate: 0.0,
    
    // Employer Information
    employer_name: '',
    employer_address_line1: '',
    employer_address_line2: '',
    employer_city: '',
    employer_state: '',
    employer_zip_code: '',
    employer_ein: '',
    
    // Deduction Categories
    deduction_categories: [
      { name: 'Health Insurance', is_active: true },
      { name: 'Dental Insurance', is_active: true },
      { name: 'Vision Insurance', is_active: true },
      { name: '401(k)', is_active: true },
      { name: 'Life Insurance', is_active: true },
      { name: 'Parking', is_active: false },
      { name: 'Other', is_active: true }
    ]
  });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // US States with typical state income tax rates
  const stateOptions = [
    { code: '', name: 'Select State', rate: 0.0 },
    { code: 'AL', name: 'Alabama', rate: 5.0 },
    { code: 'AK', name: 'Alaska', rate: 0.0 },
    { code: 'AZ', name: 'Arizona', rate: 4.5 },
    { code: 'AR', name: 'Arkansas', rate: 6.5 },
    { code: 'CA', name: 'California', rate: 9.3 },
    { code: 'CO', name: 'Colorado', rate: 4.4 },
    { code: 'CT', name: 'Connecticut', rate: 6.99 },
    { code: 'DE', name: 'Delaware', rate: 6.6 },
    { code: 'FL', name: 'Florida', rate: 0.0 },
    { code: 'GA', name: 'Georgia', rate: 5.75 },
    { code: 'HI', name: 'Hawaii', rate: 8.25 },
    { code: 'ID', name: 'Idaho', rate: 6.0 },
    { code: 'IL', name: 'Illinois', rate: 4.95 },
    { code: 'IN', name: 'Indiana', rate: 3.23 },
    { code: 'IA', name: 'Iowa', rate: 6.8 },
    { code: 'KS', name: 'Kansas', rate: 5.7 },
    { code: 'KY', name: 'Kentucky', rate: 5.0 },
    { code: 'LA', name: 'Louisiana', rate: 4.25 },
    { code: 'ME', name: 'Maine', rate: 7.15 },
    { code: 'MD', name: 'Maryland', rate: 5.75 },
    { code: 'MA', name: 'Massachusetts', rate: 5.0 },
    { code: 'MI', name: 'Michigan', rate: 4.25 },
    { code: 'MN', name: 'Minnesota', rate: 7.05 },
    { code: 'MS', name: 'Mississippi', rate: 5.0 },
    { code: 'MO', name: 'Missouri', rate: 5.3 },
    { code: 'MT', name: 'Montana', rate: 6.75 },
    { code: 'NE', name: 'Nebraska', rate: 6.84 },
    { code: 'NV', name: 'Nevada', rate: 0.0 },
    { code: 'NH', name: 'New Hampshire', rate: 0.0 },
    { code: 'NJ', name: 'New Jersey', rate: 8.97 },
    { code: 'NM', name: 'New Mexico', rate: 5.9 },
    { code: 'NY', name: 'New York', rate: 8.82 },
    { code: 'NC', name: 'North Carolina', rate: 4.99 },
    { code: 'ND', name: 'North Dakota', rate: 2.9 },
    { code: 'OH', name: 'Ohio', rate: 3.99 },
    { code: 'OK', name: 'Oklahoma', rate: 5.0 },
    { code: 'OR', name: 'Oregon', rate: 8.75 },
    { code: 'PA', name: 'Pennsylvania', rate: 3.07 },
    { code: 'RI', name: 'Rhode Island', rate: 5.99 },
    { code: 'SC', name: 'South Carolina', rate: 7.0 },
    { code: 'SD', name: 'South Dakota', rate: 0.0 },
    { code: 'TN', name: 'Tennessee', rate: 0.0 },
    { code: 'TX', name: 'Texas', rate: 0.0 },
    { code: 'UT', name: 'Utah', rate: 4.95 },
    { code: 'VT', name: 'Vermont', rate: 6.8 },
    { code: 'VA', name: 'Virginia', rate: 5.75 },
    { code: 'WA', name: 'Washington', rate: 0.0 },
    { code: 'WV', name: 'West Virginia', rate: 6.5 },
    { code: 'WI', name: 'Wisconsin', rate: 6.27 },
    { code: 'WY', name: 'Wyoming', rate: 0.0 }
  ];

  useEffect(() => {
    if (!authLoading && (!user || !canAccessManager())) {
      setMessage({ type: 'error', text: 'Access denied. Manager or admin privileges required.' });
      return;
    }
    
    if (user && token && canAccessManager()) {
      fetchPaystubConfig();
      if (activeTab === 'paystubs') {
        fetchPaystubs();
      }
    }
  }, [user, token, authLoading, canAccessManager, activeTab]);

  const fetchPaystubConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/paystub-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setConfig({ ...config, ...response.data });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching paystub config:', error);
        setMessage({ type: 'error', text: 'Failed to load paystub configuration' });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPaystubs = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' }); // Clear any previous messages
      
      // Use the same endpoint and logic as MyDashboard
      const response = await axios.get(`${API_BASE_URL}/api/ai-posts/published?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        const publishedPosts = response.data.posts || response.data;
        
        // Filter for paystub posts (same logic as MyDashboard)
        const paystubPosts = publishedPosts.filter(post => {
          const isPaystubPost = post.platforms?.includes('paystub') || 
                              post.post_type === 'paystub' ||
                              post.agent_type === 'paystub_agent' ||
                              (post.agent_name && post.agent_name.toLowerCase().includes('paystub'));
          
          return isPaystubPost;
        });

        // Sort by created/published date (newest first)
        const sortedPaystubs = paystubPosts.sort((a, b) => {
          const dateA = new Date(a.published_at || a.created_at);
          const dateB = new Date(b.published_at || b.created_at);
          return dateB - dateA;
        });

        setAllPaystubs(sortedPaystubs);
        applyPaystubFilter(sortedPaystubs, paystubFilter);
        console.log('Fetched paystubs:', sortedPaystubs.length, 'items');
      }
    } catch (error) {
      console.error('Error fetching paystubs:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Failed to load paystubs';
      if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. Manager or admin privileges required.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Paystub endpoint not found.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const formatDateRange = (paystubData, topic) => {
    // Method 1: Try different possible field names for date ranges
    const startDate = paystubData?.start_date || paystubData?.pay_period_start;
    const endDate = paystubData?.end_date || paystubData?.pay_period_end;
    
    if (startDate && endDate) {
      const start = new Date(startDate + 'T00:00:00'); // Add time to avoid timezone issues
      const end = new Date(endDate + 'T00:00:00');
      return formatFriendlyDateRange(start, end);
    }
    
    // Method 2: Try to extract dates from topic if available
    if (topic && topic.includes('Paystub Report:')) {
      const dateMatch = topic.match(/Paystub Report:\s*(.+)/);
      if (dateMatch) {
        const dateStr = dateMatch[1];
        // Try to parse the date range from topic
        const dateParts = dateStr.split(' to ');
        if (dateParts.length === 2) {
          const start = new Date(dateParts[0] + 'T00:00:00');
          const end = new Date(dateParts[1] + 'T00:00:00');
          if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
            return formatFriendlyDateRange(start, end);
          }
        }
        return dateStr; // Return as-is if can't parse
      }
    }
    
    return 'Date range not available';
  };

  const formatFriendlyDateRange = (startDate, endDate) => {
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'
    ];
    
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    const startMonth = monthNames[startDate.getMonth()];
    const startDay = startDate.getDate();
    const endMonth = monthNames[endDate.getMonth()];
    const endDay = endDate.getDate();
    
    const startFormatted = `${startMonth} ${startDay}${getOrdinalSuffix(startDay)}`;
    const endFormatted = `${endMonth} ${endDay}${getOrdinalSuffix(endDay)}`;
    
    return `${startFormatted} to ${endFormatted}`;
  };

  const handleViewPaystub = (postId) => {
    window.open(`/paystub-report/${postId}?from=paystub-settings&tab=paystubs`, '_blank');
  };

  const applyPaystubFilter = (allPaystubsData, filter) => {
    const now = new Date();
    let filteredPaystubs = [];

    switch (filter) {
      case 'latest_10':
        filteredPaystubs = allPaystubsData.slice(0, 10);
        break;
      
      case 'last_6_months':
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        filteredPaystubs = allPaystubsData.filter(paystub => {
          const paystubDate = new Date(paystub.published_at || paystub.created_at);
          return paystubDate >= sixMonthsAgo;
        });
        break;
      
      case 'this_year':
        const currentYear = now.getFullYear();
        filteredPaystubs = allPaystubsData.filter(paystub => {
          const paystubDate = new Date(paystub.published_at || paystub.created_at);
          return paystubDate.getFullYear() === currentYear;
        });
        break;
      
      case 'last_year':
        const lastYear = now.getFullYear() - 1;
        filteredPaystubs = allPaystubsData.filter(paystub => {
          const paystubDate = new Date(paystub.published_at || paystub.created_at);
          return paystubDate.getFullYear() === lastYear;
        });
        break;
      
      default:
        filteredPaystubs = allPaystubsData;
    }

    setPaystubs(filteredPaystubs);
  };

  const handleFilterChange = (newFilter) => {
    setPaystubFilter(newFilter);
    applyPaystubFilter(allPaystubs, newFilter);
  };

  const handleStateChange = (stateCode) => {
    const selectedState = stateOptions.find(state => state.code === stateCode);
    setConfig(prev => ({
      ...prev,
      state: stateCode,
      state_income_tax_rate: selectedState ? selectedState.rate : 0.0
    }));
  };

  const handleDeductionToggle = (index) => {
    setConfig(prev => ({
      ...prev,
      deduction_categories: prev.deduction_categories.map((cat, i) => 
        i === index ? { ...cat, is_active: !cat.is_active } : cat
      )
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      // Validation
      if (!config.employer_name.trim()) {
        setMessage({ type: 'error', text: 'Employer name is required' });
        setLoading(false);
        return;
      }

      if (!config.employer_ein.trim()) {
        setMessage({ type: 'error', text: 'Employer EIN is required' });
        setLoading(false);
        return;
      }

      await axios.post(`${API_BASE_URL}/api/paystub-config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: 'Paystub settings saved successfully!' });
    } catch (error) {
      console.error('Error saving paystub config:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save paystub settings' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto mb-4"></div>
          <p className="text-gray-600">Loading paystub settings...</p>
        </div>
      </div>
    );
  }

  if (!user || !canAccessManager()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">Manager or admin privileges required to access paystub settings.</p>
        </div>
      </div>
    );
  }

  const renderPaystubSettingsContent = () => (
    <>
      {/* Employer Information */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Building2 className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Employer Information</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
            <input
              type="text"
              value={config.employer_name}
              onChange={(e) => setConfig(prev => ({ ...prev, employer_name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter company name"
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 1</label>
            <input
              type="text"
              value={config.employer_address_line1}
              onChange={(e) => setConfig(prev => ({ ...prev, employer_address_line1: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter street address"
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 2 (Optional)</label>
            <input
              type="text"
              value={config.employer_address_line2}
              onChange={(e) => setConfig(prev => ({ ...prev, employer_address_line2: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Apartment, suite, etc."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
            <input
              type="text"
              value={config.employer_city}
              onChange={(e) => setConfig(prev => ({ ...prev, employer_city: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter city"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
            <select
              value={config.employer_state}
              onChange={(e) => setConfig(prev => ({ ...prev, employer_state: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {stateOptions.map(state => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code</label>
            <input
              type="text"
              value={config.employer_zip_code}
              onChange={(e) => setConfig(prev => ({ ...prev, employer_zip_code: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter ZIP code"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center">
                <Hash className="h-4 w-4 mr-1" />
                Employer Identification Number (EIN)
              </div>
            </label>
            <input
              type="text"
              value={config.employer_ein}
              onChange={(e) => setConfig(prev => ({ ...prev, employer_ein: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="XX-XXXXXXX"
            />
          </div>
        </div>
      </div>

      {/* Tax Rates */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <DollarSign className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Tax Rates (%)</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Social Security</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={config.social_security_rate}
              onChange={(e) => setConfig(prev => ({ ...prev, social_security_rate: parseFloat(e.target.value) || 0 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Medicare</label>
            <input
              type="number"
              step="0.01"
              min="0"  
              max="100"
              value={config.medicare_rate}
              onChange={(e) => setConfig(prev => ({ ...prev, medicare_rate: parseFloat(e.target.value) || 0 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Federal Income Tax</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={config.federal_income_tax_rate}
              onChange={(e) => setConfig(prev => ({ ...prev, federal_income_tax_rate: parseFloat(e.target.value) || 0 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                State
              </div>
            </label>
            <select
              value={config.state}
              onChange={(e) => handleStateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {stateOptions.map(state => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="md:col-span-2 lg:col-span-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">State Income Tax Rate</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={config.state_income_tax_rate}
              onChange={(e) => setConfig(prev => ({ ...prev, state_income_tax_rate: parseFloat(e.target.value) || 0 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Automatically populated based on state selection"
            />
            <p className="text-sm text-gray-500 mt-1">
              Rate is automatically populated when you select a state, but can be manually adjusted
            </p>
          </div>
        </div>
      </div>

      {/* Deduction Categories */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <FileText className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Deduction Categories</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {config.deduction_categories.map((category, index) => (
            <div key={index} className="flex items-center p-3 border border-gray-200 rounded-lg">
              <input
                type="checkbox"
                checked={category.is_active}
                onChange={() => handleDeductionToggle(index)}
                className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-3 text-sm font-medium text-gray-700">
                {category.name}
              </label>
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-2">
          Select which deduction categories should be available when generating paystubs
        </p>
      </div>
    </>
  );

  const renderPaystubsContent = () => (
    <>
      {/* Header with Label and Filter Dropdown */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-gray-900">Paystub Reports</h3>
        <div className="relative">
          <select
            value={paystubFilter}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="block w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-theme-blue sm:text-sm"
          >
            <option value="latest_10">Latest 10 Paystub Reports</option>
            <option value="last_6_months">Last 6 Months</option>
            <option value="this_year">This Year</option>
            <option value="last_year">Last Year</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-theme-blue border-t-transparent rounded-full"></div>
          <span className="ml-3 text-gray-600">Loading paystubs...</span>
        </div>
      ) : paystubs.length === 0 ? (
        <div className="text-center py-12">
          <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Paystubs Found</h3>
          <p className="text-gray-600 max-w-md mx-auto">
            {paystubFilter === 'latest_10' ? 
              'No published paystubs are available yet. Paystubs will appear here once they are generated and published.' :
              `No paystubs found for the selected time period. Try selecting a different filter option.`
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {paystubs.map((paystub) => {
            // Get paystub data - check both paystub_data and agent_data for compatibility
            const paystubData = paystub.paystub_data || paystub.agent_data || {};
            const employeePaystubs = paystubData.employee_paystubs || [];
            const totalEmployees = employeePaystubs.length;
            const totalPayroll = employeePaystubs.reduce((sum, emp) => sum + (emp.gross_pay || 0), 0);
            const totalHours = employeePaystubs.reduce((sum, emp) => sum + (emp.total_hours || 0), 0);
            const totalDeductions = employeePaystubs.reduce((sum, emp) => sum + (emp.total_deductions || 0), 0);
            const totalNetPay = employeePaystubs.reduce((sum, emp) => sum + (emp.net_pay || 0), 0);
            
            // Debug logging to see the data structure
            console.log('Paystub data structure:', {
              id: paystub.id,
              paystub_data: paystub.paystub_data,
              agent_data: paystub.agent_data,
              topic: paystub.topic,
              paystubData: paystubData
            });
            
            return (
              <div key={paystub.id} className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Receipt className="h-6 w-6 text-green-500" />
                    <div>
                      <h4 className="text-base font-medium text-gray-900">
                        {formatDateRange(paystubData, paystub.topic)}
                      </h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                        <span>Generated: {new Date(paystub.created_at).toLocaleDateString()}</span>
                        <span>Employees: {totalEmployees}</span>
                        {paystub.agent_name && paystub.agent_name !== 'Paystub Report' && (
                          <span>Agent: {paystub.agent_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleViewPaystub(paystub.id)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Detailed Paystub Report
                    </button>
                  </div>
                </div>
                
                {/* Summary Grid - Same style as MyDashboard */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <span className="block text-sm font-medium text-gray-600 mb-1">Total Hours:</span>
                    <p className="text-2xl font-bold text-gray-900">{totalHours.toFixed(2)}h</p>
                  </div>
                  <div>
                    <span className="block text-sm font-medium text-gray-600 mb-1">Gross Pay:</span>
                    <p className="text-2xl font-bold text-gray-900">${totalPayroll.toFixed(2)}</p>
                  </div>
                  <div>
                    <span className="block text-sm font-medium text-gray-600 mb-1">Deductions:</span>
                    <p className="text-2xl font-bold text-red-600">-${totalDeductions.toFixed(2)}</p>
                  </div>
                  <div>
                    <span className="block text-sm font-medium text-gray-600 mb-1">Net Pay:</span>
                    <p className="text-2xl font-bold text-green-600">${totalNetPay.toFixed(2)}</p>
                  </div>
                </div>
                
                {/* Employee Breakdown - Expandable Section */}
                {employeePaystubs.length > 0 && (
                  <div className="mt-6">
                    <details className="group">
                      <summary className="flex items-center cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                        <span>Employee Breakdown ({employeePaystubs.length} employees)</span>
                        <div className="ml-2 group-open:rotate-180 transition-transform">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </summary>
                      
                      <div className="mt-4 space-y-3">
                        {employeePaystubs.map((emp, index) => (
                          <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center space-x-2">
                                <User className="h-4 w-4 text-gray-500" />
                                <span className="font-medium text-gray-900">{emp.user_name}</span>
                                <span className="text-sm text-gray-500">({emp.user_role})</span>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-gray-600">Total Hours:</span>
                                <p className="font-medium">{emp.total_hours?.toFixed(2) || '0.00'}h</p>
                              </div>
                              <div>
                                <span className="text-gray-600">Gross Pay:</span>
                                <p className="font-medium">${emp.gross_pay?.toFixed(2) || '0.00'}</p>
                              </div>
                              <div>
                                <span className="text-gray-600">Deductions:</span>
                                <p className="font-medium text-red-600">-${emp.total_deductions?.toFixed(2) || '0.00'}</p>
                              </div>
                              <div>
                                <span className="text-gray-600">Net Pay:</span>
                                <p className="font-medium text-green-600">${emp.net_pay?.toFixed(2) || '0.00'}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Manage Paystubs
                  </h2>
                </div>
                {activeTab === 'settings' && (
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="text-white text-sm px-3 py-1.5 rounded-md font-medium transition-colors flex items-center justify-center gap-2"
                    style={{ 
                      backgroundColor: loading ? 'rgb(156, 163, 175)' : 'var(--theme-primary)',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = 'var(--theme-secondary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) {
                        e.target.style.backgroundColor = 'var(--theme-primary)';
                      }
                    }}
                  >
                    <Save className="h-4 w-4" />
                    <span>{loading ? 'Saving...' : 'Save Settings'}</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('settings')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'settings'
                    ? 'border-theme-blue text-theme-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-4 w-4" />
                  <span>Paystub Settings</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('paystubs')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'paystubs'
                    ? 'border-theme-blue text-theme-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Receipt className="h-4 w-4" />
                  <span>Paystubs</span>
                </div>
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 flex items-center p-4 rounded-lg ${
                message.type === 'error' 
                  ? 'bg-red-50 text-red-800 border border-red-200' 
                  : 'bg-green-50 text-green-800 border border-green-200'
              }`}>
                {message.type === 'error' ? (
                  <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
                ) : (
                  <CheckCircle className="h-5 w-5 mr-3 flex-shrink-0" />
                )}
                <span>{message.text}</span>
              </div>
            )}

            {/* Tab Content */}
            {activeTab === 'settings' ? (
              // Paystub Settings Tab Content
              <div className="space-y-8">{renderPaystubSettingsContent()}</div>
            ) : (
              // Paystubs Tab Content
              <div className="space-y-6">{renderPaystubsContent()}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaystubSettings;